/**
 * ALIGNMENT NOTE: This Heap v5 implementation should be kept in sync with:
 * - phoenix-marketing: packages/client-utils/src/analytics.ts
 *
 * Both packages use the same Heap v5 self-hosting approach with CloudFront proxy.
 * When making changes here, consider if the same changes should be applied there.
 */

import type { AnalyticsConfig, HeapSDK } from './types'

const DEFAULT_HEAP_CDN = 'https://h-cdn.prod.knock.com'
const DEFAULT_HEAP_API = 'https://h-api.prod.knock.com'
// Heap SDK version - check for updates: https://developers.heap.io/docs/heapjs-5-changelog
const DEFAULT_HEAP_SDK_VERSION = '5.3.8'
const READY_TIMEOUT_MS = 10000

type ReadyState = 'idle' | 'initializing' | 'ready' | 'failed'

let state: ReadyState = 'idle'
let readyPromise: Promise<boolean> | null = null
// Must be cleared alongside readyPromise and state if a reset/cleanup function is ever added
let racePromise: Promise<boolean> | null = null
let readyResolve: ((success: boolean) => void) | null = null
let logger: (...args: unknown[]) => void = () => {}

function createStubMethod(name: string): (...args: unknown[]) => void {
  return function (...args: unknown[]) {
    logger('Stub method called:', name, 'queuing to heapReadyCb')
    window.heapReadyCb.push({
      name: name,
      fn: function () {
        logger('heapReadyCb executing:', name)
        const heap = window.heap
        if (heap && typeof heap[name as keyof HeapSDK] === 'function') {
          ;(heap[name as keyof HeapSDK] as (...args: unknown[]) => void).apply(heap, args)
        } else {
          logger('heapReadyCb: method not found or not a function:', name, typeof heap?.[name as keyof HeapSDK])
        }
      },
    })
  }
}

/**
 * Returns a promise that resolves to true when Heap is ready, or false if
 * initialization failed, timed out, or was never started.
 */
export function waitForReady(): Promise<boolean> {
  if (state === 'ready') return Promise.resolve(true)
  if (state === 'failed') return Promise.resolve(false)

  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      readyResolve = resolve
    })
  }

  if (!racePromise) {
    racePromise = Promise.race([
      readyPromise,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), READY_TIMEOUT_MS)),
    ])
  }

  return racePromise
}

/**
 * Initialize Heap v5 with self-hosting configuration.
 * Safe to call multiple times - subsequent calls are no-ops.
 */
export function initHeap(config: AnalyticsConfig): void {
  const {
    heapToken,
    heapCdn = DEFAULT_HEAP_CDN,
    heapApi = DEFAULT_HEAP_API,
    heapSdkVersion = DEFAULT_HEAP_SDK_VERSION,
    debug = false,
  } = config

  logger = debug ? console.debug.bind(console, '[analytics]') : () => {}

  if (state !== 'idle') {
    logger('Heap already initialized, state:', state)
    return
  }

  if (typeof window === 'undefined') {
    logger('Not in browser environment')
    return
  }

  state = 'initializing'
  logger('Initializing Heap:', heapToken, 'cdn:', heapCdn, 'api:', heapApi)

  // Create ready promise if not already created by waitForReady()
  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      readyResolve = resolve
    })
  }

  // Initialize heap stub
  window.heapReadyCb = window.heapReadyCb || []
  window.heap = window.heap || ([] as unknown as HeapSDK)

  const heap = window.heap

  // Create v5 load function
  heap.load = function (
    envId: string,
    clientConfig?: { sdk?: { domain: string; version: string }; ingestServer?: string }
  ) {
    window.heap.envId = envId
    window.heap.clientConfig = clientConfig || {}
    window.heap.clientConfig.shouldFetchServerConfig = false

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.setAttribute('data-cookieyes', 'cookieyes-functional')
    script.src = heapCdn + '/config/' + envId + '/heap_config.js'

    logger('Creating script tag for:', script.src)

    const el = document.getElementsByTagName('script')[0]
    el.parentNode?.insertBefore(script, el)

    logger('Script tag inserted into DOM')

    // Create stub methods that queue calls until SDK is ready
    const methods = [
      'init',
      'startTracking',
      'stopTracking',
      'track',
      'resetIdentity',
      'identify',
      'identifyHashed',
      'getSessionId',
      'getUserId',
      'getIdentity',
      'addUserProperties',
      'addEventProperties',
      'removeEventProperty',
      'clearEventProperties',
      'addAccountProperties',
      'addAdapter',
      'addTransformer',
      'addTransformerFn',
      'onReady',
      'addPageviewProperties',
      'removePageviewProperty',
      'clearPageviewProperties',
      'trackPageview',
    ] as const

    for (const method of methods) {
      ;(heap as unknown as Record<string, unknown>)[method] = createStubMethod(method)
    }

    logger('Stub methods created')

    script.onload = function () {
      logger('heap_config.js loaded, calling heap.onReady')
      // heap_config.js loaded - use window.heap (real SDK) not captured stub reference
      try {
        window.heap.onReady(function () {
          const heapId = window.heap?.getUserId?.() || window.heap?.userId
          logger('Heap ready callback fired, heapId:', heapId)
          state = 'ready'
          readyResolve?.(true)
        })
      } catch (err) {
        console.error('[analytics] Heap onReady error:', err)
        state = 'failed'
        readyResolve?.(false)
      }
    }

    script.onerror = function (e) {
      console.error('[analytics] Failed to load heap_config.js', e)
      state = 'failed'
      readyResolve?.(false)
    }
  }

  // Load heap with v5 config
  heap.load(heapToken, {
    sdk: { domain: heapCdn, version: heapSdkVersion },
    ingestServer: heapApi,
  })

  logger('heap.load() called')
}

export function isHeapReady(): boolean {
  return state === 'ready'
}
